import { LitElement, html, css } from 'lit'
import { property, customElement } from 'lit/decorators.js'
import type { StackAttributes } from 'zaptic-stack'
import { booleanAttributeSchema, oneOfStringsAttributeSchema } from './helpers/yup'

type Align = StackAttributes['align']
type Space = StackAttributes['space']

const convertVerticalAttributeToSchema = (value: unknown) => {
    try {
        return booleanAttributeSchema.validateSync(value)
    } catch (ignore) {
        return false
    }
}

const convertAlignAttributeToPropertty = (value: unknown) => {
    try {
        const alignSchema = oneOfStringsAttributeSchema('start', 'center', 'end')
        return alignSchema.validateSync(value)
    } catch (ignore) {
        return null
    }
}

const convertSpaceAttributeToProperty = (value: unknown) => {
    try {
        const spaceSchema = oneOfStringsAttributeSchema('around', 'between', 'none')
        return spaceSchema.validateSync(value)
    } catch (ignore) {
        return null
    }
}

@customElement('zaptic-stack')
export class Stack extends LitElement implements StackAttributes {
    static styles = css`
        :host {
            display: flex;
            height: 100%;
            width: 100%;
            align-items: center;
            justify-content: space-around;
        }

        :host([vertical]) {
            flex-direction: column;
        }

        :host([align='start']) {
            align-items: flex-start;
        }

        :host([align='end']) {
            align-items: flex-end;
        }

        :host([space='between']) {
            justify-content: space-between;
        }

        :host([space='none']) {
            justify-content: normal;
        }
    `

    @property({
        reflect: true,
        type: String,
        converter: convertAlignAttributeToPropertty,
    })
    align: Align

    @property({
        reflect: true,
        type: String,
        converter: convertSpaceAttributeToProperty,
    })
    space: Space

    @property({
        reflect: true,
        type: Boolean,
        converter: convertVerticalAttributeToSchema,
    })
    vertical = false

    updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('vertical') && this.vertical === false) {
            this.removeAttribute('vertical')
        }
    }

    render() {
        return html`<slot></slot>`
    }
}
